import React from "react";
import { useTheme } from "@carrier-io/air-react/styles";
import Box from "@carrier-io/air-react/Box";
import Button from "@carrier-io/air-react/Button";
import Stack from "@carrier-io/air-react/Stack";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import { CreateUserHead } from "./CreateUserHead";
import { Clients, useQuery } from "../../apollo";
import { GetAllRolesQuery } from "../../graphql/queries/getAllRoles";
import { ControllableAutocomplete } from "../controllable/Autocomplete";
import { Control, FieldErrors } from "react-hook-form";
import { UserInput } from "../../validations/userCreateValidation";
import { Skeleton } from "@carrier-io/air-react";
import { GetAllGroupsQuery } from "../../graphql/queries/getAllGroups";

interface CreateUserRoleAndGroupProp {
  control: Control<UserInput>;
  errors: Pick<FieldErrors<UserInput>, "groups" | "roles">;
}

export const CreateUserRoleAndGroup = ({
  control,
  errors,
}: CreateUserRoleAndGroupProp) => {
  const theme = useTheme();
  const showAddNewRoleButton = false;
  const showAddNewGroupButton = false;
  const { data: roles, loading: isRolesLoading } = useQuery<{
    getAllRoles: Array<string>;
  }>(Clients.UPS, GetAllRolesQuery);

  const { data: groups, loading: isGroupsLoading } = useQuery<{
    getAllGroups: Array<string>;
  }>(Clients.UPS, GetAllGroupsQuery);

  return (
    <Box
      data-testid="user-role-group-section"
      sx={{
        paddingBottom: 3,
        marginBottom: 3,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box data-testid="group-selection-section" sx={{ marginBottom: 2 }}>
        <CreateUserHead
          title="User Role and Group"
          description="Specify the group(s) for this user. Multiple selections are possible."
        />
        {isGroupsLoading ? (
          <Skeleton
            data-testid="groups-loading-skeleton"
            height={93}
            width={300}
          />
        ) : (
          <>
            <Stack direction="row" spacing={1} sx={{ marginBottom: 2 }}>
              <ControllableAutocomplete
                multiple
                disableCloseOnSelect
                data-testid="group-autocomplete-wrapper"
                options={groups?.getAllGroups ?? []}
                error={errors?.groups?.message}
                name="groups"
                control={control}
                id="groups"
                size="xlarge"
                sx={{ maxWidth: "491px", width: "100%" }}
                textFieldProps={{
                  size: "xlarge",
                  label: "Select user group",
                  placeholder: "Select user group",
                  hideBackgroundColor: true,
                  showBorder: true,
                  required: true,
                  "data-testid": "group-autocomplete-input",
                }}
              />
            </Stack>
            {showAddNewGroupButton && (
              <Button
                data-testid="add-custom-group-button"
                variant="text"
                startIcon={<AddOutlinedIcon fontSize="small" />}
                sx={{
                  height: "inherit !important",
                  padding: 0,
                  ":hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                Add a Custom Group
              </Button>
            )}
          </>
        )}
      </Box>

      <Box data-testid="role-selection-section">
        <CreateUserHead description="Specify a role for the user " />
        {isRolesLoading ? (
          <Skeleton
            data-testid="roles-loading-skeleton"
            height={93}
            width={300}
          />
        ) : (
          <Stack spacing={2} alignItems="flex-start">
            <ControllableAutocomplete
              multiple
              disableCloseOnSelect
              options={roles?.getAllRoles ?? []}
              error={errors?.roles?.message}
              name="roles"
              control={control}
              id="roles"
              size="xlarge"
              sx={{ maxWidth: "300px", width: "100%" }}
              textFieldProps={{
                size: "xlarge",
                label: "Select user role",
                placeholder: "Select user role",
                hideBackgroundColor: true,
                showBorder: true,
                required: true,
                "data-testid": "role-autocomplete-input",
              }}
            />
            {showAddNewRoleButton && (
              <Button
                data-testid="add-new-role-button"
                variant="text"
                startIcon={<AddOutlinedIcon fontSize="small" />}
                sx={{
                  height: "inherit !important",
                  padding: 0,
                  ":hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                Add a New Role
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
};
